import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Shield, Users, AlertCircle, Scale, CheckCircle } from 'lucide-react';
import { useTermsOfService } from '../../hooks/useTermsOfService';

const iconMap = {
  FileText,
  Shield,
  Users,
  AlertCircle,
  Scale,
  CheckCircle,
};

const TermsOfService = () => {
  const { data: termsResponse, isLoading, error } = useTermsOfService();
  const termsData = termsResponse?.data;
  
  const sections = useMemo(() => {
    if (!termsData?.sections) return [];
    return termsData.sections.map(section => ({
      ...section,
      icon: iconMap[section.icon] || FileText
    }));
  }, [termsData]);

  const settings = useMemo(() => {
    return termsData?.settings || {};
  }, [termsData]);

  const lastUpdated = settings?.last_updated || "November 2, 2025";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section - extends behind header, icon animated */}
      <div className="bg-gradient-to-br from-black to-gray-900 text-white pt-20 sm:pt-24 pb-12 sm:pb-16 -mt-16">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-white/10 backdrop-blur-sm rounded-full mb-4 sm:mb-6 animate-float">
              <FileText className="w-6 h-6 sm:w-8 sm:h-8 animate-pulse-slow" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
              Terms of Service
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-4 sm:mb-6">
              Please read these terms carefully before using our services
            </p>
            <p className="text-xs sm:text-sm text-gray-400">
              Last Updated: {lastUpdated}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      {!isLoading && sections.length > 0 && (
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
          <div className="page-container px-4 sm:px-6">
            <div className="flex gap-2 sm:gap-4 overflow-x-auto py-3 sm:py-4 scrollbar-hide">
              {sections.map((section, index) => (
                <a
                  key={section.id || index}
                  href={`#section-${index}`}
                  className="text-xs sm:text-sm text-gray-600 hover:text-black whitespace-nowrap px-2 sm:px-3 py-1 rounded-full hover:bg-gray-100 transition-colors"
                >
                  {section.title}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="page-container py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          {settings?.introduction && (
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-5 sm:p-6 md:p-8 mb-6 sm:mb-8">
              <div className="text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-line">
                {settings.introduction}
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8 mb-4 sm:mb-6">
              <div className="animate-pulse space-y-4">
                <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8 mb-4 sm:mb-6">
              <p className="text-sm sm:text-base text-gray-600">
                Failed to load terms of service. Please try again later.
              </p>
            </div>
          )}

          {/* Terms Sections */}
          {!isLoading && sections.length > 0 && sections.map((section, index) => {
            const IconComponent = section.icon || FileText;
            return (
              <div
                key={section.id || index}
                id={`section-${index}`}
                className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-5 sm:p-6 md:p-8 mb-4 sm:mb-6 scroll-mt-24"
              >
                <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-black rounded-lg sm:rounded-xl flex items-center justify-center">
                    <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                      {section.title}
                    </h2>
                  </div>
                </div>
                <div className="space-y-3 sm:space-y-4 pl-0 sm:pl-16">
                  {section.content && section.content.map((paragraph, pIndex) => (
                    <p key={pIndex} className="text-sm sm:text-base text-gray-700 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Empty State */}
          {!isLoading && sections.length === 0 && (
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8 mb-4 sm:mb-6">
              <p className="text-sm sm:text-base text-gray-600 text-center">
                No terms of service sections available at this time.
              </p>
            </div>
          )}

          {/* Contact Section */}
          <div className="bg-gradient-to-br from-gray-900 to-black text-white rounded-xl sm:rounded-2xl p-6 sm:p-8 mt-8 sm:mt-12">
            <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Questions About These Terms?</h2>
            <p className="text-sm sm:text-base text-gray-300 mb-4 sm:mb-6 leading-relaxed">
              If you have any questions about these Terms of Service, please don't hesitate to contact us. We're here to help clarify anything you need.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-5 sm:px-6 py-2.5 sm:py-3 bg-white text-black rounded-lg sm:rounded-xl font-semibold hover:bg-gray-100 transition-colors text-sm sm:text-base"
              >
                Contact Support
              </Link>
              <Link
                to="/privacy"
                className="inline-flex items-center justify-center px-5 sm:px-6 py-2.5 sm:py-3 border-2 border-white text-white rounded-lg sm:rounded-xl font-semibold hover:bg-white/10 transition-colors text-sm sm:text-base"
              >
                View Privacy Policy
              </Link>
            </div>
          </div>

          {/* Agreement Acknowledgment */}
          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg sm:rounded-r-xl p-4 sm:p-6 mt-6 sm:mt-8">
            <p className="text-sm sm:text-base text-gray-800 leading-relaxed">
              <strong>By using VendaList, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</strong> If you do not agree to these terms, please do not use our services.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
